import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Col, Form, Input, Modal, Row, Select } from 'antd';
import { useEffect, useState } from 'react';

import { OptionSource } from '@/enums';
import { AdminAnswerOptionSet } from '@/types/apis/admin';

const { TextArea } = Input;
const { Option } = Select;

interface AnswerOptionSetWithOptions
  extends Omit<AdminAnswerOptionSet, 'createdAt' | 'updatedAt' | 'options'> {
  description: string;
  options: {
    id?: string;
    value: string;
    label: string;
    order?: number;
  }[];
}

interface SortableOptionProps {
  option: {
    id?: string;
    value: string;
    label: string;
    order?: number;
    uniqueId: string;
  };
  index: number;
  onOptionChange: (
    index: number,
    field: 'value' | 'label',
    value: string,
  ) => void;
  onRemoveOption: (index: number) => void;
}

function SortableOption({
  option,
  index,
  onOptionChange,
  onRemoveOption,
}: SortableOptionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.uniqueId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Row
      key={index}
      gutter={8}
      align="middle"
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'dragging' : ''}
    >
      <Col span={2}>
        <Button
          type="text"
          icon={<HolderOutlined />}
          size="small"
          style={{ cursor: 'grab' }}
          {...attributes}
          {...listeners}
        />
      </Col>
      <Col span={9}>
        <Input
          placeholder="選項值"
          value={option.value}
          onChange={(e) => onOptionChange(index, 'value', e.target.value)}
          size="small"
        />
      </Col>
      <Col span={9}>
        <Input
          placeholder="顯示文字"
          value={option.label}
          onChange={(e) => onOptionChange(index, 'label', e.target.value)}
          size="small"
        />
      </Col>
      <Col span={4}>
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => onRemoveOption(index)}
          size="small"
          danger
        />
      </Col>
    </Row>
  );
}

type Props = {
  isOpen: boolean;
  answerOptionSet?: AdminAnswerOptionSet | null;
  onClose: () => void;
  onSave: (answerOptionSet: AnswerOptionSetWithOptions) => void;
  mode: 'add' | 'edit';
};

export default function AnswerOptionSetModal({
  isOpen,
  answerOptionSet,
  onClose,
  onSave,
  mode,
}: Props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<
    Array<{
      id?: string;
      value: string;
      label: string;
      order?: number;
      uniqueId: string;
    }>
  >([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && answerOptionSet) {
        const initialValues = {
          name: answerOptionSet.name,
          description: answerOptionSet.description || '',
          optionSource: answerOptionSet.optionSource,
        };
        form.setFieldsValue(initialValues);
        const sortedOptions = (answerOptionSet.options || []).sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        const optionsWithUniqueIds = sortedOptions.map((option, index) => ({
          ...option,
          uniqueId: option.id || `temp-${Date.now()}-${index}`,
        }));
        setOptions(optionsWithUniqueIds);
      } else if (mode === 'add') {
        form.resetFields();
        form.setFieldsValue({
          optionSource: OptionSource.Static,
        });
        setOptions([]);
      }
    }
  }, [answerOptionSet, mode, isOpen, form]);

  const handleAddOption = () => {
    const newOption = {
      value: '',
      label: '',
      order: options.length,
      uniqueId: `temp-${Date.now()}-${options.length}`,
    };
    setOptions([...options, newOption]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex(
        (option) => option.uniqueId === active.id,
      );
      const newIndex = options.findIndex(
        (option) => option.uniqueId === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOptions = arrayMove(options, oldIndex, newIndex);
        // Update order based on new positions
        const updatedOptions = newOptions.map((option, index) => ({
          ...option,
          order: index,
        }));
        setOptions(updatedOptions);
      }
    }
  };

  const handleOptionChange = (
    index: number,
    field: 'value' | 'label',
    value: string,
  ) => {
    const newOptions = [...options];
    if (newOptions[index]) {
      newOptions[index] = {
        ...newOptions[index],
        [field]: value,
      };
    }
    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    newOptions.forEach((option, idx) => {
      option.order = idx;
    });
    setOptions(newOptions);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const saveData: AnswerOptionSetWithOptions = {
        id: mode === 'edit' && answerOptionSet ? answerOptionSet.id : '',
        name: values.name,
        description: values.description || '',
        optionSource: values.optionSource,
        options: options.map((option) => ({
          id: option.id,
          value: option.value,
          label: option.label,
          order: option.order,
        })),
      };

      onSave(saveData);
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setOptions([]);
    onClose();
  };

  const optionSource = Form.useWatch('optionSource', form);

  return (
    <Modal
      title={mode === 'add' ? '新增答案選項集' : '編輯答案選項集'}
      open={isOpen}
      onOk={handleSave}
      onCancel={handleCancel}
      okText={mode === 'add' ? '新增' : '更新'}
      cancelText="取消"
      width={800}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label="名稱"
          name="name"
          rules={[{ required: true, message: '請輸入名稱' }]}
        >
          <Input placeholder="請輸入選項集名稱" />
        </Form.Item>

        <Form.Item label="描述" name="description">
          <TextArea rows={3} placeholder="請輸入選項集描述" />
        </Form.Item>

        <Form.Item label="選項來源" name="optionSource">
          <Select>
            <Option value={OptionSource.Static}>靜態選項</Option>
            <Option value={OptionSource.Industry}>行業動態</Option>
          </Select>
        </Form.Item>

        {optionSource === OptionSource.Static && (
          <Form.Item label="選項內容">
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <span>自定義選項</span>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddOption}
                  size="small"
                >
                  新增選項
                </Button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={options.map((option) => option.uniqueId)}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {options.map((option, index) => (
                      <SortableOption
                        key={option.uniqueId}
                        option={option}
                        index={index}
                        onOptionChange={handleOptionChange}
                        onRemoveOption={handleRemoveOption}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {options.length === 0 && (
                <div>尚未新增任何選項，點擊上方按鈕新增選項</div>
              )}
            </div>
          </Form.Item>
        )}

        {optionSource === OptionSource.Industry && (
          <Alert
            message="行業動態選項"
            description="選擇「行業動態」將自動從系統中的行業資料載入選項內容。"
            type="info"
            showIcon
          />
        )}
      </Form>
    </Modal>
  );
}
